import { useCallback, useEffect, useMemo, useState } from 'react';

import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { parsePlaceDetails } from '../shared/placeDetailsParser';
import type { AddressDetails, GeoScope, Place, PlaceWithDetails } from '../shared/types';

import { usePlaceDetails } from './usePlaceDetails';
import { usePlacePredictions } from './usePlacePredictions';

type PlaceWithDetailsFieldHookProps = {
	geoScope?: GeoScope;
	defaultPlaceValue: PlaceWithDetails | null;
	onSelect?: (place: PlaceWithDetails | null) => void;
	inputLengthApiCallThreshold?: number;
};

type PlaceWithDetailsFieldState = {
	placeInputValue: string;
	onPlaceInputChange: (newInputValue: string) => void;
	placeValue: PlaceWithDetails | null;
	onPlaceChange: (newValue: Place | null) => void;
	placePredictions: Place[];
};

export const usePlaceWithDetailsField = ({
	geoScope,
	defaultPlaceValue,
	onSelect,
	inputLengthApiCallThreshold,
}: PlaceWithDetailsFieldHookProps): PlaceWithDetailsFieldState => {
	const [placeInputValue, setPlaceInputValue] = useState('');
	const [selectedPrediction, setSelectedPrediction] = useState<Place | null>(defaultPlaceValue);
	const [placeWithDetails, setPlaceWithDetails] = useState<PlaceWithDetails | null>(null);

	const placePredictionsResult = usePlacePredictions({ input: placeInputValue, geoScope, inputLengthApiCallThreshold });
	const placePredictions = useMemo(
		() => (isLoaded(placePredictionsResult) ? placePredictionsResult.data : []),
		[placePredictionsResult],
	);
	const placeDetailsResult = usePlaceDetails(selectedPrediction?.googlePlacesId);

	const onPlaceInputChange = (newValue: string) => {
		setPlaceInputValue(newValue);
	};

	const onPlaceChange = useCallback(
		(newSelectedPrediction: Place | null) => {
			if (isLoading(placePredictionsResult)) return;
			setSelectedPrediction(newSelectedPrediction);
		},
		[placePredictionsResult],
	);

	useEffect(() => {
		if (!selectedPrediction) return;
		if (!isLoaded(placeDetailsResult)) return;
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (!placeDetailsResult.data) return;
		const addressDetails: AddressDetails = parsePlaceDetails(placeDetailsResult.data);
		const newPlaceWithDetails: PlaceWithDetails = { ...selectedPrediction, ...addressDetails };
		if (newPlaceWithDetails.googlePlacesId !== selectedPrediction.googlePlacesId) return;

		// eslint-disable-next-line react-hooks/set-state-in-effect
		setPlaceWithDetails(newPlaceWithDetails);
		onSelect?.(newPlaceWithDetails);
	}, [selectedPrediction, placeDetailsResult, onSelect]);

	return {
		placeInputValue,
		onPlaceInputChange,
		placeValue: placeWithDetails,
		onPlaceChange,
		placePredictions,
	};
};
